import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react"
import SKLand from "@skland/one-bridge"
import classNames from "classnames"
import { useLandscapeDisplayInfo } from "@hg/layouts"
import {
    renderAccountDialog,
    useAccountLogout,
    useRoleLogout,
    useRoleMeta,
    useShowRoleSelect,
} from "@hg/one-account"
import { LoginChannelSelectRenderer } from "@hg/one-account"

import { CustomLoginModalProps } from "@/components/Layout"
import { useTranslation } from "@/locales"
import { auth } from "@/utils/auth"
import Reg from "@/utils/reg"
import { webLogout } from "@/utils/sdk"
import Exit from "../../components/Exit"
import InputPassword from "../../components/InputPassword"
import InputSmsCode from "../../components/InputSmsCode"
import Toast from "../../components/Toast"
import { GlobalContext } from "../../context"
import { useGameStoreInit } from "../Game/store"

import styles from "./index.module.less"

const isSKLand = SKLand.getSystemInfo().isApp

interface PageRegistryProps extends CustomLoginModalProps {
    className?: string
    style?: React.CSSProperties
}

const PageRegistry: React.FC<PageRegistryProps> = (props) => {
    const { t, dict: i18nCommonData } = useTranslation()
    const {
        className,
        style,
        sendSMS,
        smsCountDown,
        loginByPassword,
        loginByCode: loginCode,
    } = props

    const {
        state: { user, hasRole, showRegistry },
        dispatch,
    } = useContext(GlobalContext)
    const { inRotate, width, height } = useLandscapeDisplayInfo()

    const pageRef = useRef<HTMLDivElement>(null)

    const [channelSelected, setChannelSelected] = useState(false)

    const [account, setAccount] = React.useState("")
    const [password, setPassword] = React.useState("")

    const [smsCode, setSmsCode] = React.useState("")
    const [loginByCode, setLoginByCode] = useState(false)

    const roleLogout = useRoleLogout()
    const accountLogout = useAccountLogout()

    const handleLoginSucceed = useCallback(() => {
        dispatch({
            type: "updateContext",
            payload: { showRegistry: false },
        })
    }, [dispatch])

    const handleSelectHgChannel = useCallback(() => {
        setChannelSelected(true)
    }, [])

    const handleLogin = useCallback(async () => {
        if (loginByCode) {
            if (!Reg.isPhone.test(account)) {
                Toast.message({ content: i18nCommonData["tips.errorPhone"] })
                return
            }

            if (!smsCode) {
                Toast.message({ content: i18nCommonData["tips.noSmsCode"] })
                return
            }
        } else {
            if (!account) {
                Toast.message({ content: i18nCommonData["tips.noAccount"] })
                return
            }

            if (!password) {
                Toast.message({ content: i18nCommonData["tips.noPassword"] })
                return
            }
        }

        const loginRequest = loginByCode ? loginCode : loginByPassword
        const { result, data, msg } = await loginRequest(
            account,
            loginByCode ? smsCode : password
        )
        if (result && data?.token) {
            handleLoginSucceed()
            window.setTimeout(() => setChannelSelected(false), 500)
        } else {
            Toast.message({ content: msg })
        }
    }, [
        account,
        handleLoginSucceed,
        i18nCommonData,
        loginByCode,
        loginByPassword,
        loginCode,
        password,
        smsCode,
    ])

    const handleLogout = useCallback(async () => {
        await webLogout()
        auth.clearToken()

        setAccount("")
        setPassword("")
        setSmsCode("")
        setChannelSelected(false)

        dispatch({
            type: "updateContext",
            payload: { showRegistry: false },
        })

        window.setTimeout(() => {
            dispatch({
                type: "updateContext",
                payload: { user: null, hasRole: true },
            })
            // if (currentPage !== EPage.Loading) {
            //     window.location.reload()
            // }
        }, 500)
    }, [accountLogout, dispatch, roleLogout])

    const loginButton = useMemo(() => {
        const showLogin = channelSelected && !user
        const showLogout = (user || !hasRole) && !isSKLand

        return (
            <button
                className={classNames(styles.button, {
                    [styles.hide]: !showLogin && !showLogout,
                })}
                onClick={showLogin ? handleLogin : handleLogout}
            >
                {showLogin
                    ? i18nCommonData["login.login"]
                    : i18nCommonData["login.logout"]}
            </button>
        )
    }, [
        channelSelected,
        handleLogin,
        handleLogout,
        hasRole,
        i18nCommonData,
        user,
    ])

    const handleKeyUp = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            const target = e.target as HTMLInputElement

            if (e.key === "Enter") {
                handleLogin()
                target.blur()
            }
        },
        [handleLogin]
    )

    const handleSendSmsCode = useCallback(async () => {
        if (smsCountDown > 0) {
            return
        }

        const _account = account.trim()
        if (!_account || !Reg.isPhone.test(_account)) {
            Toast.message({ content: i18nCommonData["tips.errorPhone"] })
            return
        }

        const { result, msg } = await sendSMS(_account)
        if (!result) {
            msg && Toast.message({ content: msg })
        }
    }, [smsCountDown, account, sendSMS, i18nCommonData])

    const loginForm = useMemo(() => {
        if (!channelSelected) {
            return null
        }

        if (loginByCode) {
            return (
                <InputSmsCode
                    account={account}
                    smsCode={smsCode}
                    smsCountDown={smsCountDown}
                    onChange={({ account, smsCode }) => {
                        setAccount(account)
                        setSmsCode(smsCode)
                    }}
                    onKeyUp={handleKeyUp}
                    onSendSmsCode={handleSendSmsCode}
                />
            )
        }

        return (
            <InputPassword
                account={account}
                password={password}
                onChange={({ account, password }) => {
                    setAccount(account)
                    setPassword(password)
                }}
                onKeyUp={handleKeyUp}
            />
        )
    }, [
        account,
        channelSelected,
        handleKeyUp,
        handleSendSmsCode,
        loginByCode,
        password,
        smsCode,
        smsCountDown,
    ])

    const handleExit = useCallback(() => {
        if (!user && channelSelected) {
            setChannelSelected(false)
            setAccount("")
            setPassword("")
            setSmsCode("")
        } else {
            dispatch({
                type: "updateContext",
                payload: { showRegistry: false },
            })
        }
    }, [channelSelected, dispatch, user])

    useEffect(() => {
        document.body.style.overflow = showRegistry ? "hidden" : ""
    }, [showRegistry])

    const pageStyles = useMemo(() => {
        if (inRotate) {
            return {
                width: height,
                height: width,
                transformOrigin: "left top",
                transform: `rotate(-90deg) translate3d(-${height}px, 0, 0)`,
            }
        }

        return {
            width,
            height,
        }
    }, [height, inRotate, width])

    useGameStoreInit()

    return (
        <div
            ref={pageRef}
            className={classNames(
                styles.registry,
                inRotate && styles.rotate,
                showRegistry && styles.show,
                className
            )}
            style={{
                ...pageStyles,
                ...style,
            }}
        >
            <Exit className={styles.exit} onClick={handleExit} />
            <LoginChannelSelectRenderer
                render={({ channels, currentAccount }) => (
                    <div className={styles.box}>
                        <div className={styles.ray} />
                        <div className={styles.title}>
                            <div className={styles.mainTitle}>
                                {t("registry.title")}
                            </div>
                            {/*<div className={styles.subtitle}>*/}
                            {/*    {"EXPLORER   INFO"}*/}
                            {/*</div>*/}
                        </div>
                        <div className={classNames(styles.center)}>
                            <div
                                className={classNames(
                                    styles.unLogin,
                                    (user || !hasRole) && styles.hide,
                                    currentAccount &&
                                        !channelSelected &&
                                        !user &&
                                        styles.large
                                )}
                            >
                                <div className={classNames(styles.steps)}>
                                    <div
                                        className={classNames(
                                            styles.wrapper,
                                            channelSelected && styles.show
                                        )}
                                    >
                                        <div
                                            className={classNames(
                                                styles.step,
                                                styles.channelSelect
                                            )}
                                        >
                                            <>
                                                {currentAccount &&
                                                !channelSelected ? (
                                                    <QuickLogin
                                                        displayName={
                                                            currentAccount.displayName
                                                        }
                                                        onLogin={async () => {
                                                            handleLoginSucceed()
                                                            await currentAccount.onLogin()
                                                        }}
                                                    />
                                                ) : null}
                                                {!isSKLand ? (
                                                    <div
                                                        className={classNames(
                                                            styles.channels,
                                                            currentAccount &&
                                                                !channelSelected &&
                                                                styles.quickLogin
                                                        )}
                                                    >
                                                        {channels.map(
                                                            (item, index) => (
                                                                <div
                                                                    key={index}
                                                                    className={
                                                                        styles.channel
                                                                    }
                                                                    data-channel={
                                                                        item.channel
                                                                    }
                                                                    onClick={
                                                                        item.channel ===
                                                                        "bilibili"
                                                                            ? item.onSelect
                                                                            : handleSelectHgChannel
                                                                    }
                                                                />
                                                            )
                                                        )}
                                                    </div>
                                                ) : null}
                                            </>
                                        </div>
                                        <div
                                            className={classNames(
                                                styles.step,
                                                styles.form
                                            )}
                                        >
                                            <div
                                                className={styles.loginType}
                                                onClick={() =>
                                                    setLoginByCode(
                                                        (val) => !val
                                                    )
                                                }
                                            >
                                                {loginByCode
                                                    ? i18nCommonData[
                                                          "login.code"
                                                      ]
                                                    : i18nCommonData[
                                                          "login.password"
                                                      ]}
                                            </div>
                                            {loginForm}
                                        </div>
                                    </div>
                                </div>
                                <div style={style} className={styles.tips}>
                                    <div className={styles.main}>
                                        {i18nCommonData["login.tips"]}
                                    </div>
                                    <div className={styles.sub}>
                                        YOU MAY REGISTER
                                    </div>
                                </div>
                            </div>
                            <DoctorInfo />
                        </div>
                        {loginButton}
                        <footer>
                            {`"Crave not the thousand forking paths of the Garden of Grotesqueries\nContent yourself with but a single of them"`}
                        </footer>
                    </div>
                )}
            />
        </div>
    )
}

const QuickLogin: React.FC<{
    displayName?: string
    onLogin?: () => Promise<void>
}> = ({ displayName, onLogin }) => {
    const { dict } = useTranslation()

    return (
        <>
            <div className={styles.current} onClick={onLogin}>
                <div className={classNames(styles.currentLoginTitle)}>
                    {dict["login.by.history"]}
                </div>
                <div className={styles.currentAccount}>{displayName}</div>
            </div>
            {!isSKLand ? (
                <div className={classNames(styles.divider)}>
                    {dict["login.or"]}
                </div>
            ) : null}
        </>
    )
}

const DoctorInfo: React.FC = () => {
    const {
        state: { user, hasRole },
    } = useContext(GlobalContext)
    const { dict: i18nCommonData } = useTranslation()

    const roleMeta = useRoleMeta()
    const isBilibili = useMemo(() => {
        return roleMeta?.channel !== "account"
    }, [roleMeta])
    const showRoleSelect = useShowRoleSelect()

    return (
        <div
            className={classNames(
                styles.doctorInfo,
                (user || !hasRole) && styles.show
            )}
        >
            <div className={styles.title}>
                <div className={styles.main}>
                    {i18nCommonData["login.current"]}
                </div>
                {/*<div className={styles.sub}>REGISTERED</div>*/}
            </div>
            <div
                className={styles.nickname}
                onClick={() => {
                    !isBilibili && showRoleSelect()
                }}
            >
                <div className={styles.label}>
                    {hasRole ? `${user?.nickName}` : i18nCommonData["no.role"]}
                </div>
                {/*{isBilibili ? null : <div className={styles.switch} />}*/}
            </div>
        </div>
    )
}

export const LoginView: React.FC<{
    className?: string
    style?: React.CSSProperties
}> = (props) => {
    return renderAccountDialog((renderAccountDialogProps) => (
        <PageRegistry {...props} {...renderAccountDialogProps} />
    ))
}
